import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Loader2, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { type ApiKeyRow, createApiKey, deleteApiKey, listApiKeys, revealApiKey } from '../api';

/**
 * Gestão de chaves de API (Bearer) usadas por integrações externas (n8n e
 * outros) pra autenticar os endpoints documentados na aba "Documentação".
 * Token aparece só uma vez na criação — guardamos apenas o hash.
 */
export function ApiKeysTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['api-keys'], queryFn: listApiKeys });
  const [creating, setCreating] = useState(false);
  const items = q.data?.items ?? [];

  const deleteMut = useMutation({
    mutationFn: deleteApiKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
    onError: () => toast.error('Falha ao excluir a chave'),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Chave de API
          </h2>
          <p className="text-[13px] text-n-500">
            Tokens Bearer pra integrações externas (n8n etc.) autenticarem os endpoints da aba
            Documentação.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" />
          Nova chave
        </Button>
      </header>

      <div className="flex min-h-0 flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <Th>Nome</Th>
              <Th>Chave</Th>
              <Th>Último uso</Th>
              <Th>Status</Th>
              <th className="w-[80px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-12 text-center text-n-500">
                  Nenhuma chave criada.
                </td>
              </tr>
            )}
            {items.map((r) => (
              <ApiKeyRowView
                key={r.id}
                row={r}
                onDelete={async () => {
                  const ok = await confirm({
                    title: `Excluir "${r.name}"?`,
                    description:
                      'A chave é apagada de vez e a integração que a usa para de funcionar na hora. Não dá pra desfazer.',
                    confirmText: 'Excluir',
                    variant: 'danger',
                  });
                  if (ok) deleteMut.mutate(r.id);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {creating && <CreateApiKeyModal onClose={() => setCreating(false)} />}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="whitespace-nowrap px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500">
      {children}
    </th>
  );
}

function ApiKeyRowView({ row, onDelete }: { row: ApiKeyRow; onDelete: () => void }) {
  const revoked = row.revoked_at != null;
  const [copyState, setCopyState] = useState<'idle' | 'loading' | 'done'>('idle');

  const copy = async () => {
    setCopyState('loading');
    try {
      const { token } = await revealApiKey(row.id);
      await navigator.clipboard.writeText(token);
      setCopyState('done');
      toast.success('Chave copiada para a área de transferência');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch (err) {
      setCopyState('idle');
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Falha ao copiar a chave');
      }
    }
  };

  return (
    <tr
      className={`border-b border-n-100 last:border-b-0 hover:bg-n-50 ${revoked ? 'opacity-60' : ''}`}
    >
      <td className="px-[14px] py-[11px] font-medium text-n-900">{row.name}</td>
      <td className="px-[14px] py-[11px] font-mono text-n-600">{row.token_prefix}…</td>
      <td className="px-[14px] py-[11px] text-n-600">
        {row.last_used_at ? new Date(row.last_used_at).toLocaleString('pt-BR') : '—'}
      </td>
      <td className="px-[14px] py-[11px]">
        <span
          className={
            revoked
              ? 'rounded-pill bg-n-100 px-2 py-0.5 text-[11px] font-semibold text-n-600'
              : 'rounded-pill bg-success-50 px-2 py-0.5 text-[11px] font-semibold text-success-700'
          }
        >
          {revoked ? 'Revogada' : 'Ativa'}
        </span>
      </td>
      <td className="px-[14px] py-[11px] text-right">
        <div className="inline-flex items-center gap-1">
          {!revoked && row.copyable && (
            <button
              type="button"
              onClick={copy}
              disabled={copyState === 'loading'}
              aria-label="Copiar chave"
              title="Copiar a chave inteira"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-n-500 hover:bg-n-100 hover:text-brand-600 disabled:opacity-50"
            >
              {copyState === 'loading' ? (
                <Loader2 size={15} className="animate-spin" />
              ) : copyState === 'done' ? (
                <Check size={15} className="text-success-600" />
              ) : (
                <Copy size={15} strokeWidth={1.75} />
              )}
            </button>
          )}
          {!revoked && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Excluir chave"
              title="Excluir"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-danger-600 hover:bg-danger-50"
            >
              <Trash2 size={15} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function CreateApiKeyModal({ onClose }: { onClose: () => void }) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mut = useMutation({
    mutationFn: () => createApiKey(name.trim()),
    onSuccess: (res) => {
      setToken(res.token);
      void qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: () => toast.error('Falha ao criar a chave'),
  });

  const copy = async () => {
    if (!token) return;
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !mut.isPending) onClose();
      }}
    >
      <div className="w-[460px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-bold text-[16px] text-n-900">
            <KeyRound size={18} aria-hidden="true" /> {token ? 'Chave criada' : 'Nova chave de API'}
          </h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>

        {!token ? (
          <>
            <div className="mt-4">
              <label className="flex flex-col gap-1">
                <span className="font-semibold text-[12px] text-n-700">Nome</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Automação n8n — categorização"
                  className="rounded-md border border-n-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500"
                  // biome-ignore lint/a11y/noAutofocus: foco no único campo do modal
                  autoFocus
                />
                <span className="text-[11px] text-n-500">
                  Só pra você identificar onde a chave é usada.
                </span>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
                Cancelar
              </Button>
              <Button onClick={() => mut.mutate()} disabled={mut.isPending || !name.trim()}>
                {mut.isPending && <Loader2 size={12} className="animate-spin" />}
                Criar chave
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 rounded-md bg-brand-50 px-3 py-2 text-[12px] text-brand-700">
              Copie a chave e cole na sua integração. Se precisar dela de novo, é só usar o botão de{' '}
              <b>copiar</b> na lista de chaves.
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-md border border-n-200 bg-n-50 px-3 py-2">
              <code className="flex-1 break-all font-mono text-[12px] text-n-900">{token}</code>
              <button
                type="button"
                onClick={copy}
                className="inline-flex h-8 shrink-0 items-center gap-1 rounded bg-brand-500 px-2.5 text-[12px] font-semibold text-white hover:bg-brand-600"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <Button onClick={onClose}>Concluir</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
